import CompaniesService from "@/services/CompaniesService";

import { TProjectEditFormValues, TProjectPayload } from "@/types";

/**
 * The form's values as `POST/PATCH /projects` takes them. A client typed
 * in place is created first - the one step of the form that talks to
 * the API - so every caller (modal, settings, onboarding) gets it.
 */
export const toProjectPayload = async (
	values: TProjectEditFormValues
): Promise<TProjectPayload> => {
	const payload: TProjectPayload = {
		name: values.name.trim(),
		color: values.color,
		description: values.description?.trim() || undefined,
		ticketPrefixes: values.ticketPrefixes ?? [],
	};

	if (values.internal) {
		payload.internal = true;
		payload.rate = null;
		if (values.workspaceId !== undefined)
			payload.workspaceId = values.workspaceId;
		return payload;
	}

	if (values.isNewClient) {
		// The new client, and so the project under it, go where the form says.
		const client = await CompaniesService.create(
			{
				name: values.newClientName.trim(),
				...(values.workspaceId !== undefined && {
					workspaceId: values.workspaceId,
				}),
			},
			"clients"
		);
		payload.clientId = client.id;
	} else if (values.client) {
		payload.clientId = values.client.id;
	}

	payload.rate =
		values.rate.trim() === ""
			? null
			: Number(values.rate.trim().replace(",", "."));

	return payload;
};

/** The form's initial values for an existing project. */
export const toProjectFormValues = (project: {
	name: string;
	color: string;
	description?: string;
	client: { id: number; name: string; color: string } | null;
	rate: number | null;
	ticketPrefixes?: string[];
}): Partial<TProjectEditFormValues> => ({
	name: project.name,
	color: project.color,
	description: project.description ?? "",
	client: project.client
		? ({ ...project.client } as TProjectEditFormValues["client"])
		: null,
	internal: project.client === null,
	rate: project.rate === null ? "" : String(project.rate),
	ticketPrefixes: project.ticketPrefixes ?? [],
});
